import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  MousePointerClick,
  Target,
  Wallet,
  Percent,
  Lock,
  Loader2,
  RefreshCcw,
  ChevronDown,
  Check,
  Calendar,
  ArrowLeft,
  Download,
  Image as ImageIcon,
  Instagram,
  ArrowRight,
} from "lucide-react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { cn } from "../../../utils/function";
import useRequest from "../../../components/hook/use-req";
import { UserResponse } from "../../../components/types";
import { AiInsightsProvider, AskableValue, ExecutiveSummary, InsightNotes, usePanelInsights } from "../ai-insights";

// Rebuilt to match the shared "Ads Creative Performance" Figma screenshots
// pixel-for-pixel where the real API can back it — same host/auth as the
// Insights Overview dashboard (every path below is registered on
// api.connect.dalmlabs.com per GET /openapi.json, confirmed live). Response
// bodies aren't typed in the spec (empty schemas), so field names are a
// best-effort reading of each endpoint's own description, read defensively
// via the same unwrapValue/readMetric convention as cxo-dashboard/index.tsx
// (confirmed live elsewhere on this API: values can come back bare,
// `{value, change_pct}`, `{value, indicator}`, or `{available:false}`).
//
// Several sections in the shared mockups (ConvoAI Scores, Performance
// Overview, Creative Performance Comparison, the detail page's Performance
// Trend) reuse metric names and repeated placeholder values (e.g. "4.8%"
// and "₦1.46" on nearly every row) that also appear, unchanged, on the
// unrelated Insights Overview mockups — a sign this is recycled Figma lorem
// content, not real per-creative data. None of the `/cxo-dashboard/ads/*`
// endpoints document these fields, so rather than inventing numbers for
// them, each one is read defensively from the real response and falls back
// to a locked/"not available yet" state — same honesty convention as the
// rest of this dashboard.

type DateRange = "Last 24 Hours" | "Last 7 Days" | "Last 30 Days" | "Quarter to date";
const DATE_RANGES: DateRange[] = ["Last 24 Hours", "Last 7 Days", "Last 30 Days", "Quarter to date"];
const AD_FORMATS = ["all", "feed", "story", "reels", "explore"];
const SORT_OPTIONS = ["ctr", "clicks", "impressions", "spend", "conversions"];

const fmtDate = (d: Date) => d.toISOString().slice(0, 10);
const fmtLabel = (d: Date) => d.toLocaleDateString(undefined, { day: "numeric", month: "long" });
function presetToDates(preset: DateRange): { start_date: string; end_date: string } {
  const end = new Date();
  const start = new Date();
  if (preset === "Last 24 Hours") start.setDate(end.getDate() - 1);
  else if (preset === "Last 7 Days") start.setDate(end.getDate() - 7);
  else if (preset === "Last 30 Days") start.setDate(end.getDate() - 30);
  else start.setMonth(Math.floor(end.getMonth() / 3) * 3, 1);
  return { start_date: fmtDate(start), end_date: fmtDate(end) };
}

function unwrapValue(raw: any): any {
  if (raw == null) return raw;
  if (typeof raw === "object" && !Array.isArray(raw) && "value" in raw) return raw.value;
  return raw;
}

function readMetric(raw: any): { value: number | null; delta: number | null; available: boolean } {
  if (raw == null) return { value: null, delta: null, available: false };
  if (typeof raw === "object" && raw.available === false) return { value: null, delta: null, available: false };
  if (typeof raw === "object" && !Array.isArray(raw)) {
    const value = unwrapValue(raw) ?? raw.total ?? null;
    const delta = raw.change_pct ?? raw.delta_pct ?? raw.change ?? null;
    return { value, delta, available: value != null };
  }
  return { value: raw, delta: null, available: true };
}

const formatPct = (n: any) => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? "—" : `${Number(v).toFixed(1)}%`;
};
const formatCurrency = (n: any) => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? "—" : `₦${Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
};
const formatCount = (n: any) => {
  const v = unwrapValue(n);
  return v == null || isNaN(Number(v)) ? "—" : Math.round(Number(v)).toLocaleString();
};
const formatDelta = (pct: number | null) => (pct == null ? null : `${pct > 0 ? "+" : ""}${pct.toFixed(1)}%`);
const asText = (raw: any): string => {
  const v = unwrapValue(raw);
  return v == null ? "—" : String(v);
};
const ordinal = (n: number) => {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1:
      return `${n}st`;
    case 2:
      return `${n}nd`;
    case 3:
      return `${n}rd`;
    default:
      return `${n}th`;
  }
};

const CheckDropdown: React.FC<{ value: string; options: string[]; onChange: (v: string) => void; prefix?: string; icon?: React.ReactNode }> = ({
  value,
  options,
  onChange,
  prefix,
  icon,
}) => {
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        className="flex items-center gap-2 px-3.5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors whitespace-nowrap capitalize"
      >
        {icon}
        {prefix ? `${prefix}: ${value}` : value}
        <ChevronDown size={14} className={cn("text-gray-400 dark:text-gray-500 transition-transform", open ? "rotate-180" : "")} />
      </button>
      {open && (
        <div className="absolute right-0 z-20 mt-1 min-w-full w-max rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-lg overflow-hidden py-1">
          {options.map((opt) => (
            <button
              key={opt}
              type="button"
              onClick={() => {
                onChange(opt);
                setOpen(false);
              }}
              className="flex items-center gap-2 w-full text-left px-3.5 py-2 text-sm whitespace-nowrap hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-200 capitalize"
            >
              <Check size={14} className={cn("shrink-0", opt === value ? "text-amber-600 dark:text-amber-400" : "text-transparent")} />
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Cosmetic-only period pill shown on individual cards in the mockup
// ("Selected Period", "Last 30 days") — these aren't independently
// filterable in the real API (only the page-level date range and the ad
// format filter are), so this just mirrors the active range rather than
// pretending to be its own control.
const PeriodPill: React.FC<{ label: string }> = ({ label }) => (
  <span className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-500 dark:text-gray-400 border border-gray-200 dark:border-gray-700 rounded-full whitespace-nowrap shrink-0">
    {label}
    <ChevronDown size={12} className="text-gray-400 dark:text-gray-500" />
  </span>
);

// Per the updated design: every locked insight reads the same bold title
// ("Available in Upcoming Release") with a lock icon in a dark rounded
// square to its left, rather than each card inventing its own "currently
// unavailable" wording — only the detail line underneath varies per card.
// Same component as cxo-dashboard/index.tsx's own copy.
const UpcomingReleaseCard: React.FC<{ detail: string }> = ({ detail }) => (
  <div className="flex items-start gap-4 rounded-xl bg-[#FAF9F5] dark:bg-gray-800/60 p-4 sm:p-5">
    <div className="shrink-0 flex items-center justify-center w-11 h-11 rounded-xl bg-gray-900 dark:bg-gray-700">
      <Lock size={18} className="text-white" />
    </div>
    <div>
      <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Available in Upcoming Release</p>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{detail}</p>
    </div>
  </div>
);

const LockedStatCard: React.FC<{ icon: React.ComponentType<any>; label: string; description: string }> = ({ icon: Icon, label, description }) => (
  <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
    <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
      <span className="flex items-center gap-1.5 truncate">
        <Lock size={11} className="text-gray-400 dark:text-gray-500 shrink-0" />
        {label}
      </span>
      <Icon size={14} className="text-gray-300 dark:text-gray-600 shrink-0" />
    </div>
    <p className="text-xl sm:text-2xl font-semibold text-gray-300 dark:text-gray-700 blur-[5px] select-none">0.0%</p>
    <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 leading-snug">{description}</p>
  </div>
);

const STATUS_COLORS: Record<string, string> = {
  Strongest: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  Growing: "bg-amber-50 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400",
  Stable: "bg-sky-50 text-sky-700 dark:bg-sky-500/15 dark:text-sky-400",
};

const CAMPAIGN_STATUS_COLORS: Record<string, string> = {
  active: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400",
  paused: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
  ended: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",
};

// A labelled %-bar row — used for CTA/messaging/visual elements and for the
// detail page's Click/Messaging element performance lists.
const ElementRow: React.FC<{ label: string; value: any }> = ({ label, value }) => {
  const pct = unwrapValue(value);
  const num = pct == null || isNaN(Number(pct)) ? null : Number(pct);
  return (
    <div className="py-1.5">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-600 dark:text-gray-300 truncate">{label}</span>
        <span className="font-semibold text-gray-900 dark:text-gray-100 shrink-0 ml-2">{num == null ? "—" : `${num.toFixed(1)}%`}</span>
      </div>
      <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-800 overflow-hidden">
        <div className="h-full bg-amber-400 rounded-full" style={{ width: `${Math.max(0, Math.min(100, num ?? 0))}%` }} />
      </div>
    </div>
  );
};

// Plain label/value row for heterogeneous-unit metrics (counts, currency) —
// unlike ElementRow's %-bar, these shouldn't be rendered as if they were all
// on a comparable 0–100 scale.
const StatRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div className="flex items-center justify-between text-xs py-1.5">
    <span className="text-gray-600 dark:text-gray-300 truncate">{label}</span>
    <span className="font-semibold text-gray-900 dark:text-gray-100 shrink-0 ml-2">{value}</span>
  </div>
);

interface Creative {
  ad_id: string;
  name?: string;
  ad_name?: string;
  format?: string;
  ad_format?: string;
  ctr?: number;
  clicks?: number;
  impressions?: number;
  spend?: number;
  conversions?: number;
  cpa?: number;
  cost_per_conversion?: number;
  revenue?: number;
  status?: string;
  rank?: number;
}

// ─── Creative detail — full page, matching the "Family Bundle Promo" mockup ──
const CreativeDetailView: React.FC<{
  creative: Creative;
  authHeaders: Record<string, any>;
  baseParams: Record<string, any>;
  onBack: () => void;
}> = ({ creative, authHeaders, baseParams, onBack }) => {
  const [detail, setDetail] = useState<Record<string, any> | null>(null);
  const [loading, setLoading] = useState(true);
  const { makeRequest: getDetail } = useRequest("/cxo-dashboard/ads/creatives", "GET", authHeaders);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [res, status] = await getDetail(undefined, baseParams, creative.ad_id);
      if (cancelled) return;
      setLoading(false);
      if (status === 200) setDetail(res?.data || res);
      else toast.error("Couldn't load this creative's detail.");
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creative.ad_id, JSON.stringify(baseParams)]);

  const name = asText(detail?.name ?? detail?.ad_name ?? creative.name ?? creative.ad_name ?? creative.ad_id);
  // `preview` is confirmed to come back bare `null` for imported/lifetime-only
  // ads (no Meta creative asset was ever fetched for them) — every field
  // below degrades gracefully rather than assuming a brand/image exists.
  const preview = detail?.preview || {};
  // Real shape (confirmed live): video_url takes priority when present (the
  // creative is actually a video ad) — image_url, then thumbnail_url, are
  // the stills to fall back through in that order when there's no video.
  const videoUrl = preview.video_url || null;
  const imageUrl = preview.image_url || preview.thumbnail_url || null;
  const posterUrl = preview.thumbnail_url || preview.image_url || null;
  const headline = preview.headline || null;
  // `message` is the real field for an Instagram/Facebook ad's caption text
  // (confirmed live) — kept alongside the other guesses in case a different
  // ad source uses one of those instead.
  const primaryText = preview.message || preview.primary_text || preview.ad_copy || preview.caption || null;
  const description = preview.description || preview.link_description || null;
  const cta = preview.cta || preview.call_to_action || null;
  const displayUrl = preview.link || preview.display_url || preview.website_url || null;
  const hasPreviewChrome = Boolean(videoUrl || imageUrl || headline || primaryText || description || cta || displayUrl);

  const ranking = detail?.ranking || {};
  const rank = ranking.rank ?? creative.rank ?? null;
  const totalCreatives = ranking.total ?? null;

  // Confirmed live shape: `insights` (plural, array — empty when none).
  const insight = detail?.insights ?? null;
  const insightLines: string[] = Array.isArray(insight) ? insight.map(asText) : typeof insight === "string" ? insight.split(/\n+/).filter(Boolean) : [];

  // Confirmed live: metrics are flat counts/currency on `detail.metrics`
  // (link_clicks, landing_page_views, unique_clicks, cpc, headline/messaging
  // breakdowns aren't part of this shape at all — `element_performance`
  // itself comes back bare `null`), not the nested %-based breakdown the
  // Figma mockup implied.
  const metrics = detail?.metrics || {};
  const messagingAvailable = detail?.element_performance != null;

  // `campaign`/`ad_set` come back as objects ({campaign_id, name, sent_at},
  // confirmed live) rather than plain strings — asText() on the raw object
  // would just stringify it to "[object Object]", so pull the name out first.
  const campaignName = detail?.campaign && typeof detail.campaign === "object" ? detail.campaign.name ?? null : detail?.campaign ?? null;
  const adSetName = detail?.ad_set && typeof detail.ad_set === "object" ? detail.ad_set.name ?? null : detail?.ad_set ?? null;

  const sentAt = detail?.sent_at ?? detail?.created_time ?? detail?.launched_at ?? detail?.campaign?.sent_at ?? null;
  const sentLabel = sentAt
    ? new Date(sentAt).toLocaleString(undefined, { day: "numeric", month: "long", year: "numeric", hour: "numeric", minute: "2-digit" })
    : null;
  const campaignStatus = (detail?.status || "").toString().toLowerCase();
  const isKnownStatus = ["active", "paused", "ended"].includes(campaignStatus);

  const audienceResponse = detail?.audience_response;
  const audienceLocked = !audienceResponse || audienceResponse.available === false;

  // Not part of the confirmed creative-detail shape at all (ad_id, name,
  // status, campaign, ad_set, imported, note, preview, metrics,
  // element_performance, ranking, insights, audience_response is the full
  // real response) — kept as honest locked/"not available" sections below
  // rather than invented numbers.
  const convoScores = detail?.convoai_scores || {};
  const perfOverview = detail?.performance_overview || {};
  const trendSeries: any[] = Array.isArray(detail?.performance_trend?.series) ? detail.performance_trend.series : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={22} className="animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Breadcrumb */}
      <button onClick={onBack} type="button" className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
        <ArrowLeft size={15} />
        Back to Ad Performance
        <span className="text-gray-300 dark:text-gray-600">/</span>
        <span className="text-gray-800 dark:text-gray-100 font-medium">{name}</span>
      </button>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="w-8 h-8 rounded-full bg-gradient-to-br from-pink-500 via-purple-500 to-amber-400 flex items-center justify-center text-white shrink-0">
            <Instagram size={15} />
          </span>
          <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-gray-100">{name}</h1>
          {isKnownStatus && (
            <span className={cn("px-2 py-0.5 rounded-full text-[11px] font-medium capitalize", CAMPAIGN_STATUS_COLORS[campaignStatus])}>{campaignStatus}</span>
          )}
        </div>
        <div className="flex items-center gap-3">
          {sentLabel && <p className="text-xs text-gray-400 dark:text-gray-500">Sent on {sentLabel}</p>}
          <button type="button" className="flex items-center gap-1.5 px-3.5 py-2 text-xs font-semibold text-white bg-gray-900 dark:bg-gray-100 dark:text-gray-900 rounded-lg hover:opacity-90">
            <Download size={13} /> Performance Report
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Left column */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Ad Creative Preview</h2>
            {detail?.imported && (
              <div className="rounded-lg bg-gray-50 dark:bg-gray-800/60 px-3 py-2 mb-3 text-[11px] text-gray-500 dark:text-gray-400">
                {asText(detail?.note) !== "—" ? asText(detail.note) : "Lifetime totals from the imported export — no live creative preview for this ad."}
              </div>
            )}
            {hasPreviewChrome ? (
              <div className="rounded-xl border border-gray-100 dark:border-gray-800 overflow-hidden">
                {primaryText && <p className="text-sm text-gray-700 dark:text-gray-200 px-3 pt-3 pb-1">{primaryText}</p>}
                <div className="aspect-[4/3] bg-gray-100 dark:bg-gray-800 flex items-center justify-center">
                  {videoUrl ? (
                    <video src={videoUrl} poster={posterUrl || undefined} controls className="w-full h-full object-cover" />
                  ) : imageUrl ? (
                    <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon size={28} className="text-gray-300 dark:text-gray-600" />
                  )}
                </div>
                {(displayUrl || headline || description || cta) && (
                  <div className="flex items-center justify-between px-3 py-2.5 bg-gray-50 dark:bg-gray-800/60">
                    <div className="min-w-0">
                      {displayUrl && <p className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 truncate">{asText(displayUrl)}</p>}
                      {headline && <p className="text-xs font-semibold text-gray-800 dark:text-gray-100 truncate">{asText(headline)}</p>}
                      {description && <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">{asText(description)}</p>}
                    </div>
                    {cta && <span className="shrink-0 ml-3 px-3 py-1.5 text-xs font-semibold text-white bg-gray-900 dark:bg-gray-700 rounded-lg whitespace-nowrap">{asText(cta)}</span>}
                  </div>
                )}
              </div>
            ) : (
              <div className="rounded-xl border border-gray-100 dark:border-gray-800 aspect-[4/3] bg-gray-50 dark:bg-gray-800/60 flex flex-col items-center justify-center gap-1.5">
                <ImageIcon size={24} className="text-gray-300 dark:text-gray-600" />
                <p className="text-xs text-gray-400 dark:text-gray-500">No creative preview available for this ad.</p>
              </div>
            )}
            {(adSetName || campaignName) && (
              <p className="text-[11px] text-gray-400 dark:text-gray-500 mt-2">
                {adSetName ? `Ad set: ${asText(adSetName)}` : ""}
                {adSetName && campaignName ? " · " : ""}
                {campaignName ? `Campaign: ${asText(campaignName)}` : ""}
              </p>
            )}

            {rank != null && (
              <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">Ad Ranking</p>
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">
                  {ordinal(Number(rank))}
                  {totalCreatives != null ? ` of ${totalCreatives} ad creatives you have run so far` : ""}
                </p>
              </div>
            )}

            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-800">
              <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-2">Overall Insight</p>
              {insightLines.length > 0 ? (
                <ul className="space-y-1.5">
                  {insightLines.map((line, i) => (
                    <li key={i} className="text-xs text-gray-600 dark:text-gray-300 flex gap-1.5">
                      <span className="text-amber-500 shrink-0">•</span>
                      {line}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-gray-400 dark:text-gray-500">No insight available for this creative yet.</p>
              )}
            </div>
          </div>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-0.5">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Individual Element Performance</h2>
              <PeriodPill label="Selected Period" />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Click and messaging performance for this specific ad.</p>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1">Click Performance</p>
                <StatRow label="Link Clicks" value={formatCount(metrics.link_clicks)} />
                <StatRow label="Landing Page Views" value={formatCount(metrics.landing_page_views)} />
                <StatRow label="Unique Clicks" value={formatCount(metrics.unique_clicks)} />
                <StatRow label="Cost per Click" value={formatCurrency(metrics.cpc)} />
              </div>
              <div>
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1 flex items-center gap-1">
                  {!messagingAvailable && <Lock size={9} />} Messaging Element Performance
                </p>
                {messagingAvailable ? (
                  <>
                    <StatRow label="Headline CTR" value={formatPct(detail?.element_performance?.headline_ctr)} />
                    <StatRow label="Primary Text CTR" value={formatPct(detail?.element_performance?.primary_text_ctr)} />
                    <StatRow label="CTA Click Rate" value={formatPct(detail?.element_performance?.cta_click_rate)} />
                  </>
                ) : (
                  <p className="text-xs text-gray-400 dark:text-gray-500">Not available for this ad.</p>
                )}
              </div>
            </div>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-0.5">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Audience Response Insight</h2>
              <PeriodPill label="Selected Period" />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Sentiments of audience who received this ad.</p>
            {audienceLocked ? (
              <UpcomingReleaseCard detail="This insight is planned for an upcoming release to help you understand audience response in more detail." />
            ) : (
              <pre className="text-xs text-gray-600 dark:text-gray-300 whitespace-pre-wrap">{JSON.stringify(audienceResponse, null, 2)}</pre>
            )}
          </div>
        </div>
      </div>

      {/* ConvoAI Scores */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">ConvoAI Scores</h2>
          <PeriodPill label="Last 30 days" />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
          {[
            { key: "headline_score", label: "Headline Score", description: "Clear, relevant and emotionally appealing." },
            { key: "cta_score", label: "CTA Score", description: "Performs well, but there's still room for improvement." },
            { key: "color_score", label: "Color Score", description: "Brand colour drives recognition and trust." },
            { key: "layout_score", label: "Layout and Arrangement", description: "How clean and well structured the creative is." },
            { key: "visual_focus_score", label: "Visual Focus Score", description: "How well the visual resonates with the target audience." },
          ].map((c) => {
            const metric = readMetric(convoScores[c.key]);
            return (
              <div key={c.key} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
                <p className="text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2 truncate">{c.label}</p>
                <p className={cn("text-xl sm:text-2xl font-semibold", metric.value == null ? "text-gray-300 dark:text-gray-700" : "text-gray-900 dark:text-gray-100")}>
                  {metric.value == null ? "—" : asText(metric.value)}
                </p>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 leading-snug">{metric.value == null ? "Not available yet." : c.description}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Performance Overview */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Performance Overview</h2>
          <PeriodPill label="Last 30 days" />
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {[
            // Confirmed real fields, straight off `detail.metrics`.
            { key: "reach", label: "Reach", format: formatCount, sub: "Of Total Contacts", source: "metrics" },
            { key: "impressions", label: "Impressions", format: formatCount, sub: "Targeted Contacts", source: "metrics" },
            { key: "link_clicks", label: "URL Clicks", format: formatCount, sub: "Of Delivered Audience", source: "metrics" },
            { key: "ctr", label: "CTR", format: formatPct, sub: "Of Total Contacts", source: "metrics" },
            { key: "cpc", label: "Cost Per Click", format: formatCurrency, sub: "Of Delivered Audience", source: "metrics" },
            { key: "conversion_rate", label: "Conversion Rate", format: formatPct, sub: "Of Delivered Audience", source: "metrics" },
            // Not part of the confirmed metrics shape — stay locked.
            { key: "engagement_rate", label: "Engagement Rate", format: formatPct, sub: "Of Delivered Audience", source: "overview" },
            { key: "subscriptions", label: "Subscriptions", format: formatCount, sub: "Of Delivered Audience", source: "overview" },
            { key: "total_revenue", label: "Total Revenue", format: formatCurrency, sub: "For selected period", source: "overview" },
          ].map((c) => {
            const metric = readMetric(c.source === "metrics" ? metrics[c.key] : perfOverview[c.key]);
            return (
              <div key={c.key}>
                <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400">{c.label}</p>
                <p className={cn("text-lg font-semibold mt-0.5", metric.value == null ? "text-gray-300 dark:text-gray-700" : "text-gray-900 dark:text-gray-100")}>
                  {metric.value == null ? "—" : c.format(metric.value)}
                </p>
                <p className="text-[11px] text-gray-400 dark:text-gray-500">{c.sub}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Creative Performance Comparison — explicitly locked per the API
         description ("Revenue is locked" / no comparison endpoint exists) */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-0.5">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Creative Performance Comparison Chart</h2>
          <PeriodPill label="Last 30 days" />
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Compare your current performance against historical periods.</p>
        <UpcomingReleaseCard detail="Creative performance comparisons will be available in a future release." />
      </div>

      {/* Performance Trend */}
      <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
        <div className="flex items-center justify-between mb-0.5">
          <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">Performance Trend</h2>
          <PeriodPill label="Last 30 days" />
        </div>
        <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">Trend for daily clicks and impressions on this creative.</p>
        {trendSeries.length === 0 ? (
          <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-16">No trend data for this creative yet.</p>
        ) : (
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={trendSeries} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="clicks" stroke="#f59e0b" fill="url(#trendFill)" strokeWidth={2} name="Clicks" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};

const AdsCreativePerformance: React.FC = () => {
  const token = localStorage.getItem("authToken");
  const authHeaders = { Authorization: `Bearer ${token}` };

  const [user, setUser] = useState<UserResponse | null>(null);
  const { makeRequest: getUserData } = useRequest(`/auth/get_userdata`, "GET", authHeaders);
  useEffect(() => {
    (async () => {
      const [response] = await getUserData();
      setUser(response);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [liveMode, setLiveMode] = useState<"realtime" | "fixed">("fixed");
  const [dateRange, setDateRange] = useState<DateRange>("Last 7 Days");
  const { start_date, end_date } = useMemo(() => presetToDates(dateRange), [dateRange]);
  const baseParams = useMemo(
    () => (liveMode === "realtime" ? { mode: "realtime" } : { mode: "fixed", start_date, end_date }),
    [liveMode, start_date, end_date]
  );
  const dateRangeLabel = useMemo(() => `${fmtLabel(new Date(start_date))} – ${fmtLabel(new Date(end_date))}`, [start_date, end_date]);

  const [adFormat, setAdFormat] = useState("all");
  const [sortBy, setSortBy] = useState("ctr");
  const [selectedCreative, setSelectedCreative] = useState<Creative | null>(null);

  const [overview, setOverview] = useState<Record<string, any> | null>(null);
  const [loadError, setLoadError] = useState(false);
  const { makeRequest: getOverview, loading } = useRequest("/cxo-dashboard/ads/overview", "GET", authHeaders);

  const [daily, setDaily] = useState<Record<string, any> | null>(null);
  const { makeRequest: getDaily } = useRequest("/cxo-dashboard/ads/performance/daily", "GET", authHeaders);

  const [byFormat, setByFormat] = useState<Record<string, any> | null>(null);
  const { makeRequest: getByFormat } = useRequest("/cxo-dashboard/ads/performance/by-format", "GET", authHeaders);

  const [creatives, setCreatives] = useState<Creative[]>([]);
  const { makeRequest: getCreatives } = useRequest("/cxo-dashboard/ads/creatives", "GET", authHeaders);

  const [topCreatives, setTopCreatives] = useState<Creative[]>([]);
  const { makeRequest: getTopCreatives } = useRequest("/cxo-dashboard/ads/top-creatives", "GET", authHeaders);

  const [topElements, setTopElements] = useState<Record<string, any> | null>(null);
  const { makeRequest: getTopElements } = useRequest("/cxo-dashboard/ads/top-elements", "GET", authHeaders);

  const fetchAll = React.useCallback(async () => {
    setLoadError(false);
    const [ov, status] = await getOverview(undefined, { ...baseParams, ai_insights: "false" });
    if (status === 200 && ov?.status) setOverview(ov.data || ov);
    else {
      setLoadError(true);
      toast.error(ov?.detail || ov?.message || "Failed to load Ads Creative Performance.");
    }
    const [dl] = await getDaily(undefined, { ...baseParams, ai_insights: "false" });
    setDaily(dl?.data || dl || null);
    const [bf] = await getByFormat(undefined, { ad_format: adFormat, ...baseParams, ai_insights: "false" });
    setByFormat(bf?.data || bf || null);
    const [cr] = await getCreatives(undefined, { sort_by: sortBy, limit: "25", ...baseParams });
    setCreatives(Array.isArray(cr?.items) ? cr.items : []);
    const [tc] = await getTopCreatives(undefined, { limit: "5", ...baseParams, ai_insights: "false" });
    setTopCreatives(Array.isArray(tc?.items) ? tc.items : []);
    const [te] = await getTopElements(undefined, { limit: "4", ...baseParams, ai_insights: "false" });
    setTopElements(te?.data || te || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(baseParams), adFormat, sortBy]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Panel data above loads with ai_insights=false; the AI cards load here.
  const aiInsights = usePanelInsights({
    overview: { endpoint: "/cxo-dashboard/ads/overview", params: baseParams },
    daily: { endpoint: "/cxo-dashboard/ads/performance/daily", params: baseParams },
    byFormat: { endpoint: "/cxo-dashboard/ads/performance/by-format", params: { ad_format: adFormat, ...baseParams } },
    topCreatives: { endpoint: "/cxo-dashboard/ads/top-creatives", params: { limit: 5, ...baseParams } },
    topElements: { endpoint: "/cxo-dashboard/ads/top-elements", params: { limit: 4, ...baseParams } },
  });

  const ctr = readMetric(overview?.average_ctr ?? overview?.ctr);
  const conversionRate = readMetric(overview?.conversion_rate);
  const cpc = readMetric(overview?.cost_per_click ?? overview?.cpc);
  const cpa = readMetric(overview?.cost_per_conversion ?? overview?.cpa);
  const creativeScore = readMetric(overview?.avg_creative_score);

  const unwrapRow = (row: Record<string, any>) => Object.fromEntries(Object.entries(row).map(([k, v]) => [k, unwrapValue(v)]));
  const dailySeries: any[] = (Array.isArray(daily?.series) ? daily.series : []).map(unwrapRow);
  const dailySummary = daily?.summary || {};
  const byFormatSeries: any[] = (
    Array.isArray(byFormat?.items) ? byFormat.items : Array.isArray(byFormat?.series) ? byFormat.series : Array.isArray(byFormat?.data) ? byFormat.data : []
  ).map(unwrapRow);
  const byFormatSummary = byFormat?.summary || {};

  const messagingStyleRaw = topElements?.messaging_style;
  const visualStyleRaw = topElements?.visual_style;
  const messagingList: any[] = Array.isArray(messagingStyleRaw) ? messagingStyleRaw : [];
  const visualList: any[] = Array.isArray(visualStyleRaw) ? visualStyleRaw : [];
  const messagingLocked = messagingList.length === 0 && (!messagingStyleRaw || messagingStyleRaw.available === false);
  const visualLocked = visualList.length === 0 && (!visualStyleRaw || visualStyleRaw.available === false);
  const ctaElements: any[] = Array.isArray(topElements?.items) ? topElements.items : Array.isArray(topElements?.elements) ? topElements.elements : [];

  const summaryStrip = (summary: Record<string, any>) => [
    { label: "Impressions", metric: readMetric(summary.impressions), format: formatCount },
    { label: "Clicks", metric: readMetric(summary.clicks), format: formatCount },
    { label: "Conversions", metric: readMetric(summary.conversions), format: formatCount },
    { label: "ROAS-R", metric: readMetric(summary.roas ?? summary.roas_r), format: (v: any) => `${Number(v).toFixed(1)}x` },
  ];

  if (!overview && loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-400 dark:text-gray-500">
          <Loader2 size={22} className="animate-spin" />
          <p className="text-sm">Loading Ads Creative Performance…</p>
        </div>
      </div>
    );
  }

  if (!overview && loadError) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] dark:bg-gray-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3 text-gray-500 dark:text-gray-400 text-center px-6">
          <p className="text-sm font-medium">Couldn't load Ads Creative Performance.</p>
          <button type="button" onClick={fetchAll} className="flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-amber-500 hover:bg-amber-600 rounded-lg">
            <RefreshCcw size={14} /> Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("min-h-screen bg-[#FAF9F5] dark:bg-gray-950 p-4 sm:p-6 transition-opacity", loading && "opacity-60")}>
      {selectedCreative ? (
        <CreativeDetailView creative={selectedCreative} authHeaders={authHeaders} baseParams={baseParams} onBack={() => setSelectedCreative(null)} />
      ) : (
        <div className="space-y-5">
          {/* Greeting + Real-Time/Fixed Period toggle */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">
              See how your ads are performing{user?.payload?.first_name ? `, ${user.payload.first_name}` : ""}.
            </h1>
            <div className="flex items-center gap-1 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-xl p-1 shrink-0">
              {(["realtime", "fixed"] as const).map((mode) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setLiveMode(mode)}
                  className={cn(
                    "px-3.5 py-1.5 text-sm font-medium rounded-lg transition-colors",
                    liveMode === mode ? "bg-gray-900 dark:bg-gray-100 text-white dark:text-gray-900" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                  )}
                >
                  {mode === "realtime" ? "Real-time" : "Fixed Period"}
                </button>
              ))}
            </div>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-gray-100">Ad Creative Performance</h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">See which creatives drive clicks, conversions, and stronger campaign results.</p>
            </div>
            {liveMode === "fixed" && (
              <CheckDropdown value={dateRangeLabel} options={DATE_RANGES} onChange={(v) => setDateRange(v as DateRange)} icon={<Calendar size={14} className="text-gray-400 dark:text-gray-500" />} />
            )}
          </div>

          {/* Ad Creatives Overview */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Ad Creatives Overview</h3>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              {[
                { label: "Average CTR", metric: ctr, icon: MousePointerClick, format: formatPct, description: "Average percentage of impressions that resulted in a click." },
                { label: "Conversion Rate", metric: conversionRate, icon: Target, format: formatPct, description: "Percentage of ad clicks that resulted in the desired action." },
                { label: "Cost Per Click", metric: cpc, icon: Wallet, format: formatCurrency, description: "Average amount spent for each click on your ad creatives." },
                { label: "Cost Per Conversion", metric: cpa, icon: Wallet, format: formatCurrency, description: "Average amount spent to generate one completed conversion." },
              ].map((c) => {
                const Icon = c.icon;
                const delta = formatDelta(c.metric.delta);
                return (
                  <div key={c.label} className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
                    <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
                      <span className="truncate">{c.label}</span>
                      <Icon size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                    </div>
                    <p className={cn("text-xl sm:text-2xl font-semibold", c.metric.value == null ? "text-gray-300 dark:text-gray-700" : "text-gray-900 dark:text-gray-100")}>
                      {c.metric.value == null ? "—" : <AskableValue label={c.label} value={c.format(c.metric.value)} />}
                    </p>
                    {c.metric.value == null ? (
                      <p className="text-xs text-gray-300 dark:text-gray-600 mt-1.5">Not available yet</p>
                    ) : (
                      <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5 leading-snug">
                        {delta && <span className="text-emerald-500 font-medium mr-1">↑ {delta}</span>}
                        {c.description}
                      </p>
                    )}
                  </div>
                );
              })}
              {creativeScore.available ? (
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4">
                  <div className="flex items-center justify-between text-[12px] font-semibold text-gray-500 dark:text-gray-100 mb-2">
                    <span className="truncate">Avg Creative Score</span>
                    <Percent size={14} className="text-gray-400 dark:text-gray-500 shrink-0" />
                  </div>
                  <p className="text-xl sm:text-2xl font-semibold text-gray-900 dark:text-gray-100">{asText(creativeScore.value)}</p>
                </div>
              ) : (
                <LockedStatCard icon={Percent} label="Avg Creative Score" description="Average amount spent to generate one completed conversion." />
              )}
            </div>
          </div>

          {/* AI copy is sample text until the AI insights endpoint exists. */}
          <ExecutiveSummary period={liveMode === "fixed" ? dateRangeLabel : "Real-time"} insight={aiInsights.overview} />

          {/* Clicks and Impressions */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Clicks and Impressions</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-0.5">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Daily Clicks and Impressions</h4>
                  <PeriodPill label="Selected Period" />
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-3">Trend for daily clicks and impressions on your ads.</p>
                <div className="grid grid-cols-4 gap-2 mb-3 bg-amber-50 dark:bg-amber-500/10 rounded-xl p-3">
                  {summaryStrip(dailySummary).map((s) => (
                    <div key={s.label}>
                      <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 truncate">{s.label}</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{s.metric.value == null ? "—" : <AskableValue label={s.label} value={s.format(s.metric.value)} />}</p>
                      {formatDelta(s.metric.delta) && <p className="text-[10px] text-emerald-600 dark:text-emerald-400">↑ {formatDelta(s.metric.delta)}</p>}
                    </div>
                  ))}
                </div>
                {dailySeries.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">No daily performance data for this period yet.</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailySeries} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <defs>
                          <linearGradient id="clicksFill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.35} />
                            <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis dataKey={dailySeries[0]?.date ? "date" : "day"} tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Area type="monotone" dataKey="impressions" stroke="#d4d4aa" fill="#d4d4aa" fillOpacity={0.3} strokeWidth={2} name="Impressions" />
                        <Area type="monotone" dataKey="clicks" stroke="#f59e0b" fill="url(#clicksFill)" strokeWidth={2} name="Clicks" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <InsightNotes insight={aiInsights.daily} />
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-0.5">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Performance by Format</h4>
                  <CheckDropdown
                    value={adFormat === "all" ? "All Ad Formats" : adFormat}
                    options={["All Ad Formats", ...AD_FORMATS.filter((f) => f !== "all")]}
                    onChange={(v) => setAdFormat(v === "All Ad Formats" ? "all" : v)}
                  />
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-3">Real-time performance metrics and analytics.</p>
                <div className="grid grid-cols-4 gap-2 mb-3 bg-amber-50 dark:bg-amber-500/10 rounded-xl p-3">
                  {summaryStrip(byFormatSummary).map((s) => (
                    <div key={s.label}>
                      <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 truncate">{s.label}</p>
                      <p className="text-sm font-bold text-gray-900 dark:text-gray-100">{s.metric.value == null ? "—" : <AskableValue label={s.label} value={s.format(s.metric.value)} />}</p>
                      {formatDelta(s.metric.delta) && <p className="text-[10px] text-emerald-600 dark:text-emerald-400">↑ {formatDelta(s.metric.delta)}</p>}
                    </div>
                  ))}
                </div>
                {byFormatSeries.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-12">No by-format breakdown for this period yet.</p>
                ) : (
                  <div className="h-56">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={byFormatSeries} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                        <XAxis dataKey="format" tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: "#9ca3af" }} axisLine={false} tickLine={false} />
                        <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                        <Bar dataKey="clicks" fill="#f59e0b" radius={[4, 4, 0, 0]} name="Clicks" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
                <InsightNotes insight={aiInsights.byFormat} />
              </div>
            </div>
          </div>

          {/* Top Performing */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Top Performing</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-0.5">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Top Performing Creatives</h4>
                  <PeriodPill label="Selected Period" />
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-3">Your top performing ads in the selected period.</p>
                {topCreatives.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No ranked creatives for this period yet.</p>
                ) : (
                  <div className="overflow-x-auto -mx-1">
                    <table className="w-full min-w-[480px] text-sm">
                      <thead>
                        <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                          <th className="font-semibold pb-2 pl-1">CREATIVE NAME</th>
                          <th className="font-semibold pb-2">FORMAT</th>
                          <th className="font-semibold pb-2">CTR</th>
                          <th className="font-semibold pb-2">CONV.</th>
                          <th className="font-semibold pb-2">CPA</th>
                          <th className="font-semibold pb-2">REVENUE</th>
                          <th className="font-semibold pb-2 pr-1">STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {topCreatives.map((c, i) => (
                          <tr
                            key={c.ad_id || i}
                            onClick={() => setSelectedCreative(c)}
                            className="border-t border-gray-50 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                          >
                            <td className="py-2.5 pl-1">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="w-6 h-6 rounded-lg bg-gray-100 dark:bg-gray-800 shrink-0 flex items-center justify-center">
                                  <ImageIcon size={12} className="text-gray-300 dark:text-gray-600" />
                                </span>
                                <span className="font-medium text-gray-800 dark:text-gray-100 truncate">{asText(c.name ?? c.ad_name)}</span>
                              </div>
                            </td>
                            <td className="py-2.5 text-gray-600 dark:text-gray-300 capitalize whitespace-nowrap">{asText(c.format ?? c.ad_format)}</td>
                            <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatPct(c.ctr)}</td>
                            <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCount(c.conversions)}</td>
                            <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCurrency(c.cpa ?? c.cost_per_conversion)}</td>
                            <td className="py-2.5 text-gray-400 dark:text-gray-500">{c.revenue != null ? formatCurrency(c.revenue) : <Lock size={11} className="inline" />}</td>
                            <td className="py-2.5 pr-1">
                              {c.status && (
                                <span className={cn("px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap", STATUS_COLORS[asText(c.status)] || "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300")}>
                                  {asText(c.status)}
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
                <InsightNotes insight={aiInsights.topCreatives} />
              </div>

              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
                <div className="flex items-center justify-between mb-0.5">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Top Converting Elements</h4>
                  <PeriodPill label="Selected Period" />
                </div>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-3">Your top converting elements during for active campaigns.</p>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5">CTA Elements</p>
                    {ctaElements.length === 0 ? (
                      <p className="text-[11px] text-gray-400 dark:text-gray-500">No CTA data yet.</p>
                    ) : (
                      ctaElements.slice(0, 4).map((el, i) => <ElementRow key={i} label={asText(el.label ?? el.cta ?? el.name)} value={el.ctr ?? el.conversion_rate} />)
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                      {messagingLocked && <Lock size={9} />} Messaging Style
                    </p>
                    {messagingLocked ? (
                      <p className="text-[11px] text-gray-400 dark:text-gray-500">Not available yet.</p>
                    ) : (
                      messagingList.slice(0, 4).map((el, i) => <ElementRow key={i} label={asText(el.label ?? el.name)} value={el.value ?? el.ctr} />)
                    )}
                  </div>
                  <div>
                    <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 mb-1.5 flex items-center gap-1">
                      {visualLocked && <Lock size={9} />} Visual Style
                    </p>
                    {visualLocked ? (
                      <p className="text-[11px] text-gray-400 dark:text-gray-500">Not available yet.</p>
                    ) : (
                      visualList.slice(0, 4).map((el, i) => <ElementRow key={i} label={asText(el.label ?? el.name)} value={el.value ?? el.ctr} />)
                    )}
                  </div>
                </div>
                <InsightNotes insight={aiInsights.topElements} />
              </div>
            </div>
          </div>

          {/* Next Best Actions — generic best-practice guidance (not
             per-account computed data; no API field backs this section) */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">Next Best Actions</h3>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5 flex flex-col">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Suggested Improvements</h4>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-3">Suggested improvements for your ad creatives.</p>
                <ul className="space-y-1.5 flex-1">
                  {[
                    "Test a shorter, benefit-led headline to improve CTR",
                    "Increase CTA contrast and move it higher in the layout",
                    "Reduce text density and give the product more visual focus",
                    "Create 2–3 variants using the strongest-performing composition",
                  ].map((t) => (
                    <li key={t} className="text-xs text-gray-600 dark:text-gray-300 flex gap-1.5">
                      <span className="text-amber-500 shrink-0">•</span>
                      {t}
                    </li>
                  ))}
                </ul>
                <button type="button" className="mt-4 self-start flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-900 bg-amber-400 hover:bg-amber-500 rounded-lg">
                  Create Creative Variant <ArrowRight size={13} />
                </button>
              </div>
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5 flex flex-col">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Performance Opportunities</h4>
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-3">Opportunities to explore for your ad creatives.</p>
                <ul className="space-y-1.5 flex-1">
                  {[
                    "Reuse the best-performing CTA across similar campaigns",
                    "Adapt the top creative for underperforming regions or audiences",
                    "Refresh creatives showing early signs of fatigue",
                    "Test the strongest message with a new visual treatment",
                  ].map((t) => (
                    <li key={t} className="text-xs text-gray-600 dark:text-gray-300 flex gap-1.5">
                      <span className="text-amber-500 shrink-0">•</span>
                      {t}
                    </li>
                  ))}
                </ul>
                <button type="button" className="mt-4 self-start flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-gray-900 bg-amber-400 hover:bg-amber-500 rounded-lg">
                  View Recommended Tests <ArrowRight size={13} />
                </button>
              </div>
            </div>
          </div>

          {/* All creatives, ranked */}
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
            <div className="flex items-center justify-between mb-0.5">
              <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100">All Creatives</h2>
              <CheckDropdown value={sortBy} options={SORT_OPTIONS} onChange={setSortBy} prefix="Sort by" />
            </div>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 mb-4">Every ad with its own metrics and rank — click one for the full detail.</p>
            {creatives.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-gray-500 text-center py-8">No creatives for this period yet.</p>
            ) : (
              <div className="overflow-x-auto -mx-1">
                <table className="w-full min-w-[640px] text-sm">
                  <thead>
                    <tr className="text-left text-[11px] font-semibold text-gray-400 dark:text-gray-500 tracking-wide">
                      <th className="font-semibold pb-2 pl-1">CREATIVE</th>
                      <th className="font-semibold pb-2">FORMAT</th>
                      <th className="font-semibold pb-2">CTR</th>
                      <th className="font-semibold pb-2">CLICKS</th>
                      <th className="font-semibold pb-2">IMPRESSIONS</th>
                      <th className="font-semibold pb-2 text-right pr-1">SPEND</th>
                    </tr>
                  </thead>
                  <tbody>
                    {creatives.map((c, i) => (
                      <tr
                        key={c.ad_id || i}
                        onClick={() => setSelectedCreative(c)}
                        className="border-t border-gray-50 dark:border-gray-800 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/60"
                      >
                        <td className="py-2.5 pl-1 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{asText(c.name ?? c.ad_name ?? c.ad_id)}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300 capitalize">{asText(c.format ?? c.ad_format)}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatPct(c.ctr ?? null)}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCount(c.clicks)}</td>
                        <td className="py-2.5 text-gray-600 dark:text-gray-300">{formatCount(c.impressions)}</td>
                        <td className="py-2.5 pr-1 text-right text-gray-600 dark:text-gray-300">{formatCurrency(c.spend ?? null)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const AdsCreativePerformancePage: React.FC = () => (
  <AiInsightsProvider>
    <AdsCreativePerformance />
  </AiInsightsProvider>
);

export default AdsCreativePerformancePage;
